import { curryAppSelector } from "@/store/reselect";
import { GUID } from "@faro-lotv/foundation";
import {
  CaptureTreeEntityRevision,
  RegistrationStatus,
} from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import {
  ChildrenMap,
  selectRevisionEntityChildrenMap,
  selectRootEntity,
} from "./revision/revision-entity-selectors";

/** A map from the ID of an entity to its registration status. */
export type RegistrationStatusMap = Record<
  GUID,
  RegistrationStatus | undefined
>;

/**
 * All registration statuses, sorted from "worst" to "best".
 * The worse the status, the higher precedence it should have when being shown to the user.
 */
const SORTED_STATUSES: RegistrationStatus[] = [
  RegistrationStatus.unknown,
  RegistrationStatus.unregistered,
  RegistrationStatus.unverified,
  RegistrationStatus.registered,
  RegistrationStatus.lockedManually,
];

/**
 * A map from an entity ID to its hierarchical registration status.
 * This is different from the registration status stored on the entity itself.
 * It ensures that the status is consistent with the entities contained as children in the entity.
 * This is especially useful for tree structures shown in the UI.
 */
export const selectHierarchicalRegistrationStatusMap = createSelector(
  [selectRootEntity, selectRevisionEntityChildrenMap],
  (root, childrenMap): RegistrationStatusMap => {
    const statusMap: RegistrationStatusMap = {};

    if (!root) return statusMap;

    // Compute the status recursively starting from the root
    hierarchicalRegistrationStatusRecursive(root, childrenMap, statusMap);

    return statusMap;
  },
);

/**
 * @param state The current applications state.
 * @param entityId The ID of the entity to get the hierarchical registration status for.
 * @returns the hierarchical registration status of an entity.
 * For a leaf entity, this will be the same as it's `registrationStatus` property.
 * For clusters, this will be the "worst" status among its children.
 * Falls back to `RegistrationStatus.unknown`.
 */
export const selectHierarchicalRegistrationStatus = curryAppSelector(
  createSelector(
    [
      selectHierarchicalRegistrationStatusMap,
      (state, entityId?: GUID) => entityId,
    ],
    (statusMap, entityId): RegistrationStatus =>
      entityId
        ? (statusMap[entityId] ?? RegistrationStatus.unknown)
        : RegistrationStatus.unknown,
  ),
);

/**
 * @param entity The entity to start the recursion at.
 * @param childrenMap Cached map to the children of each entity for performance.
 * @param statusMap The map where the statuses should be added to.
 *   This will be modified during execution.
 */
function hierarchicalRegistrationStatusRecursive(
  entity: CaptureTreeEntityRevision,
  childrenMap: ChildrenMap,
  statusMap: RegistrationStatusMap,
): void {
  const children = childrenMap[entity.id] ?? [];

  if (children.length === 0) {
    // Leaf node, get status from entity
    statusMap[entity.id] =
      entity.registrationStatus ?? RegistrationStatus.unknown;
    return;
  }

  // Compute the status of all children first
  for (const child of children) {
    if (!(child.id in statusMap)) {
      hierarchicalRegistrationStatusRecursive(child, childrenMap, statusMap);
    }
  }

  // Non-leaf node, get the "worst" status among children
  for (const status of SORTED_STATUSES) {
    if (children.some((child) => statusMap[child.id] === status)) {
      statusMap[entity.id] = status;
      return;
    }
  }
}
